/**
 * Integration (PMS) mutations — tenant-scoped.
 *
 * ALL NON-OPTIMISTIC: this is provider configuration (the policy's explicit
 * never-optimistic category). Credentials are write-only — no endpoint returns
 * them, so we never cache them. `test` does not mutate the integration entity,
 * so it only updates the integration detail (lastTestedAt may change) by
 * invalidation.
 */

import { type UseMutationResult, useMutation, useQueryClient } from '@tanstack/react-query';
import { unwrap } from '../queries/_helpers';
import { queryKeys } from '../query-keys';
import { useApiClient } from '../react/ApiClientProvider';
import {
  type Integration,
  integrationSchema,
  type TestConnectionResult,
  testConnectionResultSchema,
} from '../responses';
import type { ConfigureIntegrationBody, SetIntegrationStatusBody } from './_types';

function useIntegrationSync(tenantId: string) {
  const queryClient = useQueryClient();
  return (integration: Integration) => {
    queryClient.setQueryData(queryKeys.integration(tenantId, integration.id), integration);
    void queryClient.invalidateQueries({ queryKey: queryKeys.integrations(tenantId) });
    void queryClient.invalidateQueries({
      queryKey: queryKeys.integrationCapabilities(tenantId, integration.id),
    });
  };
}

/** `POST /integrations` — configure a PMS integration (credentials write-only). */
export function useConfigureIntegration(
  tenantId: string,
): UseMutationResult<Integration, unknown, ConfigureIntegrationBody> {
  const client = useApiClient();
  const sync = useIntegrationSync(tenantId);
  return useMutation({
    mutationFn: async (body: ConfigureIntegrationBody) => {
      const result = await client.POST('/api/v1/tenants/{tenantId}/integrations', {
        params: { path: { tenantId } },
        body,
        parseAs: 'json',
      });
      return unwrap(result, integrationSchema);
    },
    onSuccess: sync,
  });
}

/** `POST /integrations/:integrationId/test` — probe the connection. */
export function useTestIntegration(
  tenantId: string,
): UseMutationResult<TestConnectionResult, unknown, { integrationId: string }> {
  const client = useApiClient();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ integrationId }: { integrationId: string }) => {
      const result = await client.POST(
        '/api/v1/tenants/{tenantId}/integrations/{integrationId}/test',
        { params: { path: { tenantId, integrationId } }, parseAs: 'json' },
      );
      return unwrap(result, testConnectionResultSchema);
    },
    onSuccess: (_data, { integrationId }) => {
      void queryClient.invalidateQueries({
        queryKey: queryKeys.integration(tenantId, integrationId),
      });
    },
  });
}

export interface SetIntegrationStatusVars {
  integrationId: string;
  body: SetIntegrationStatusBody;
}

/** `POST /integrations/:integrationId/status` — set lifecycle status. */
export function useSetIntegrationStatus(
  tenantId: string,
): UseMutationResult<Integration, unknown, SetIntegrationStatusVars> {
  const client = useApiClient();
  const sync = useIntegrationSync(tenantId);
  return useMutation({
    mutationFn: async ({ integrationId, body }: SetIntegrationStatusVars) => {
      const result = await client.POST(
        '/api/v1/tenants/{tenantId}/integrations/{integrationId}/status',
        { params: { path: { tenantId, integrationId } }, body, parseAs: 'json' },
      );
      return unwrap(result, integrationSchema);
    },
    onSuccess: sync,
  });
}

/** `POST /integrations/:integrationId/disable`. */
export function useDisableIntegration(
  tenantId: string,
): UseMutationResult<Integration, unknown, { integrationId: string }> {
  const client = useApiClient();
  const sync = useIntegrationSync(tenantId);
  return useMutation({
    mutationFn: async ({ integrationId }: { integrationId: string }) => {
      const result = await client.POST(
        '/api/v1/tenants/{tenantId}/integrations/{integrationId}/disable',
        { params: { path: { tenantId, integrationId } }, parseAs: 'json' },
      );
      return unwrap(result, integrationSchema);
    },
    onSuccess: sync,
  });
}
